import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import classNames from "classnames";
import { toast } from "react-toastify";

import { markAllRead, markRead } from "../helpers/api/notifications";
import BellIcon from "./icons/BellIcon";
import styles from "./NotificationDropdown.module.scss";
import NotificationList, { timeAgo } from "./notifications/NotificationList";

export { timeAgo };


interface NotificationDropdownProps {
  // raw notifications from API; should include { id, name, description, read, data }
  notifications: any[];
  connected?: boolean; // optional socket connection indicator
  // allow parent to update cache optimistically if desired (React Query setQueryData)
  onLocalUpdate?: (updater: (prev: any[]) => any[]) => void;
}

const NotificationDropdown = ({ notifications: notificationsProp, connected, onLocalUpdate }: NotificationDropdownProps) => {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const notifications = useMemo(() => notificationsProp || [], [notificationsProp]);
  const unreadCount = useMemo(() => (notifications || []).filter((n: any) => !n?.read).length, [notifications]);

  // toggle notification-dropdown
  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const buildLink = (n: any) => {
    const link = n?.data?.link;
    if (!link || !link.pathTemplate) return "#";
    let path = link.pathTemplate as string;
    const params = link.params || {};
    Object.keys(params).forEach((k) => {
      path = path.replace(`:${k}`, encodeURIComponent(String(params[k])));
    });
    return path;
  };

  const goTo = (n: any) => {
    const path = buildLink(n);
    if (path && path !== "#") navigate(path);
  };

  const optimisticUpdate = useCallback(
    (predicate: (x: any) => boolean, patch: Partial<any>) => {
      if (onLocalUpdate) {
        onLocalUpdate((prev: any[]) => (prev || []).map((x: any) => (predicate(x) ? { ...x, ...patch } : x)));
      }
    },
    [onLocalUpdate]
  );

  const handleMarkRead = async (n: any) => {
    if (n.read) {
      goTo(n);
      return;
    }
    // optimistic
    optimisticUpdate((x) => x.id === n.id, { read: true });
    try {
      await markRead(n.id);
      goTo(n);
    } catch (e) {
      // revert and notify
      optimisticUpdate((x) => x.id === n.id, { read: false });
      toast.error("Couldn't mark notification as read");
      goTo(n);
    }
  };

  const handleMarkAll = useCallback(async () => {
    const prevUnread = (notifications || []).filter((n: any) => !n.read).map((n: any) => n.id);
    // optimistic
    optimisticUpdate(() => true, { read: true });
    try {
      await markAllRead();
    } catch (e) {
      // revert
      optimisticUpdate((x) => prevUnread.includes(x.id), { read: false });
      toast.error("Couldn't mark all notifications as read");
    }
  }, [notifications, optimisticUpdate]);

  // UX: when the tray opens, bulk mark as read optimistically — ONCE per opening.
  // A failure reverts the unread state, which used to re-trigger this effect and
  // retry forever (a stack of "Couldn't mark all…" toasts); the ref stops that.
  const autoMarked = useRef(false);
  useEffect(() => {
    if (!dropdownOpen) {
      autoMarked.current = false;
      return;
    }
    if (unreadCount > 0 && !autoMarked.current) {
      autoMarked.current = true;
      void handleMarkAll();
    }
  }, [dropdownOpen, unreadCount, handleMarkAll]);

  return (
    <Dropdown show={dropdownOpen} onToggle={toggleDropdown}>
      <Dropdown.Toggle
        id="dropdown-notification"
        role="button"
        as="a"
        onClick={toggleDropdown}
        className={classNames("nav-link waves-effect waves-light arrow-none notification-list", { show: dropdownOpen })}
      >
        <BellIcon className="noti-icon" width={18} height={18} />
        {connected === false && (
          <span
            title="Live connection lost"
            style={{ position: "absolute", top: 10, right: 10, width: 8, height: 8, backgroundColor: "#dc3545", borderRadius: "50%" }}
          />
        )}
        {unreadCount ? <span className="badge bg-danger rounded-circle noti-icon-badge">{unreadCount}</span> : null}
      </Dropdown.Toggle>
      <Dropdown.Menu align="end" className={styles.menu} popperConfig={{ modifiers: [{ name: "preventOverflow", options: { padding: 8 } }] }}>
        <div onClick={toggleDropdown}>
          <div className={styles.head}>
            <h6 className={styles.title}>Notifications</h6>
            <button type="button" onClick={handleMarkAll} className={styles.markAll}>
              Mark all read
            </button>
          </div>
          <div className={styles.list}>
            <NotificationList notifications={notifications || []} onOpen={handleMarkRead} />
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationDropdown;
